<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Descuentos y adicionales</h1>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative">

          <!-- Period selector -->
          <div class="d-flex align-items-center mb-2 mb-md-0" v-if="!showGroups">
            <FormSelect
              v-model="filters.month"
              :items="monthOptions"
              class="mr-2"
              style="min-width: 140px"
              @input="loadData"
            />
            <FormSelect
              v-model="filters.year"
              :items="yearOptions"
              class="mr-2"
              style="min-width: 100px"
              @input="loadData"
            />
            <button
              type="button"
              class="button-tooltip text-dark font-weight-bold"
              v-b-tooltip.hover
              title="Recargar"
              @click="loadData"
            >
              <i class="ri-loop-right-line"></i>
            </button>
          </div>
          <div v-else class="d-flex align-items-center mb-2 mb-md-0">
            <button
              type="button"
              class="button-tooltip text-dark font-weight-bold"
              v-b-tooltip.hover
              title="Recargar"
              @click="loadGroups"
            >
              <i class="ri-loop-right-line"></i>
            </button>
          </div>

          <!-- Filters + actions -->
          <section class="w-full w-md-auto d-flex flex-column flex-md-row align-items-center mt-2 mt-md-0">
            <template v-if="!showGroups">
              <FormSelect
                v-model="filters.type"
                :items="typeFilterOptions"
                defaultOption="Todos los tipos"
                class="mr-md-2 mb-2 mb-md-0"
                style="min-width: 160px"
                @input="loadData"
              />
              <div class="mr-md-2 mb-2 mb-md-0" style="min-width: 200px">
                <SearchableDropdown
                  :options="conceptOptions"
                  :oldValue="filters.conceptId"
                  placeholder="Todos los conceptos"
                  @selected="onConceptSelected"
                />
              </div>
              <div class="mr-md-2 mb-2 mb-md-0" style="min-width: 180px">
                <SearchableDropdown
                  :options="companyOptions"
                  :oldValue="filters.companyId"
                  placeholder="Todas las empresas"
                  @selected="onCompanySelected"
                />
              </div>
            </template>
            <div class="d-flex align-items-center mt-2 mt-md-0">
              <b-form-checkbox
                switch
                v-model="showGroups"
                @change="onToggleGroups"
                class="mr-2"
              >
                Cuotas
              </b-form-checkbox>
              <b-button
                :to="{ name: 'payroll-movement-create' }"
                variant="none"
                class="button button-primary mr-2"
              >Agregar</b-button>
              <b-button
                :to="{ name: 'payroll-movement-bulk' }"
                variant="none"
                class="button button-secondary mr-2"
              >Masivo</b-button>
              <b-button
                :to="{ name: 'payroll-movement-summary' }"
                variant="none"
                class="button button-light mr-2"
              >Resumen</b-button>
              <button
                v-if="!showGroups"
                type="button"
                class="button button-light"
                @click="downloadExcel"
                :disabled="downloading"
              >
                <i class="ri-file-excel-line mr-1"></i>
                {{ downloading ? 'Descargando...' : 'Excel' }}
              </button>
            </div>
          </section>
        </div>
      </div>

      <!-- ============================================================= -->
      <!-- VISTA NORMAL: Movimientos por período                          -->
      <!-- ============================================================= -->
      <template v-if="!showGroups">
        <!-- Desktop table -->
        <table class="custom-table w-100 d-none d-xl-table">
          <thead>
            <tr>
              <th>EMPLEADO</th>
              <th>DNI</th>
              <th>EMPRESA</th>
              <th>CONCEPTO</th>
              <th>TIPO</th>
              <th>NATURALEZA</th>
              <th class="text-right">MONTO</th>
              <th>CUOTA</th>
              <th>ORIGEN</th>
              <th>ESTADO</th>
              <th class="pr-md-3">OPCIONES</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="list.length === 0">
              <td colspan="11" class="text-center text-muted py-4">No se encontraron movimientos para el período seleccionado.</td>
            </tr>
            <tr v-for="item in list" :key="item.id">
              <td>{{ item.employeeName }}</td>
              <td>{{ item.employeeIdentifier }}</td>
              <td>{{ item.companyName }}</td>
              <td>{{ item.conceptName }}</td>
              <td>
                <span :class="`badge ${getTypeColor(item.conceptType)}`">{{ getTypeLabel(item.conceptType) }}</span>
              </td>
              <td>{{ getNatureLabel(item.conceptNature) }}</td>
              <td class="text-right">{{ formatAmount(item.amount, item.currency) }}</td>
              <td>{{ item.installmentLabel || '-' }}</td>
              <td>{{ getOriginLabel(item.origin) }}</td>
              <td>
                <span :class="`badge ${getStatusColor(item.status)}`">{{ item.status }}</span>
              </td>
              <td class="cell-center d_option pr-md-2 position-relative">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem
                      v-if="item.status === 'PENDIENTE'"
                      text="Editar"
                      :to="{ name: 'payroll-movement-edit', params: { id: item.id } }"
                    />
                    <DropdownItem
                      v-if="item.status === 'PENDIENTE'"
                      text="Cancelar"
                      @click="openCancel(item)"
                    />
                    <DropdownItem
                      v-if="item.groupId"
                      text="Ver grupo de cuotas"
                      :to="{ name: 'movement-group-detail', params: { groupId: item.groupId } }"
                    />
                    <DropdownItem
                      v-if="item.status !== 'PENDIENTE' && !item.groupId"
                      text="Ver detalle"
                      :to="{ name: 'payroll-movement-edit', params: { id: item.id } }"
                    />
                  </template>
                </DropdownTable>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Mobile -->
        <div class="custom-table-mobile d-xl-none">
          <section class="custom-table-mobile-header">
            <div><span>Empleado</span></div>
            <div><span>Estado</span></div>
          </section>
          <div class="custom-table-mobile-body">
            <div class="custom-table-mobile-row" v-for="item in list" :key="item.id">
              <div class="custom-table-mobile-grid">
                <div><span>{{ item.employeeName }}</span></div>
                <div>
                  <span :class="`badge ${getStatusColor(item.status)}`">{{ item.status }}</span>
                </div>
                <div>
                  <button type="button" class="border-0 bg-transparent" @click="item.showMobileData = !item.showMobileData">
                    <i :class="item.showMobileData ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                  </button>
                </div>
              </div>
              <div v-if="item.showMobileData" class="pt-3">
                <ul class="list-unstyled p-0">
                  <li><span class="font-weight-medium mr-2">DNI:</span>{{ item.employeeIdentifier }}</li>
                  <li><span class="font-weight-medium mr-2">Empresa:</span>{{ item.companyName }}</li>
                  <li><span class="font-weight-medium mr-2">Concepto:</span>{{ item.conceptName }}</li>
                  <li>
                    <span class="font-weight-medium mr-2">Tipo:</span>
                    <span :class="`badge ${getTypeColor(item.conceptType)}`">{{ getTypeLabel(item.conceptType) }}</span>
                  </li>
                  <li><span class="font-weight-medium mr-2">Monto:</span>{{ formatAmount(item.amount, item.currency) }}</li>
                  <li v-if="item.installmentLabel"><span class="font-weight-medium mr-2">Cuota:</span>{{ item.installmentLabel }}</li>
                  <li><span class="font-weight-medium mr-2">Origen:</span>{{ getOriginLabel(item.origin) }}</li>
                </ul>
              </div>
              <div class="custom-table-mobile-options mt-3">
                <router-link v-if="item.status === 'PENDIENTE'" :to="{ name: 'payroll-movement-edit', params: { id: item.id } }" class="mx-3">Editar</router-link>
                <a v-if="item.status === 'PENDIENTE'" href="" @click.prevent="openCancel(item)">Cancelar</a>
                <router-link v-if="item.groupId" :to="{ name: 'movement-group-detail', params: { groupId: item.groupId } }" class="mx-3">Ver grupo</router-link>
              </div>
            </div>
          </div>
        </div>

        <div class="table-footer d-flex justify-content-end align-items-center">
          <div v-if="list.length === 0" class="table-no-results">
            <p class="m-0 px-3">No se encontraron resultados.</p>
          </div>
          <p v-else class="m-0 px-3 text-muted small">{{ list.length }} registro(s)</p>
        </div>
      </template>

      <!-- ============================================================= -->
      <!-- VISTA GRUPOS: Cuotas en curso                                  -->
      <!-- ============================================================= -->
      <template v-else>
        <table class="custom-table w-100 d-none d-xl-table">
          <thead>
            <tr>
              <th>EMPLEADO</th>
              <th>DNI</th>
              <th>EMPRESA</th>
              <th>CONCEPTO</th>
              <th class="text-right">MONTO TOTAL</th>
              <th>CUOTAS</th>
              <th class="text-right">SALDO</th>
              <th>ESTADO</th>
              <th class="pr-md-3">OPCIONES</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="groups.length === 0">
              <td colspan="9" class="text-center text-muted py-4">No se encontraron grupos de cuotas.</td>
            </tr>
            <tr v-for="g in groups" :key="g.groupId">
              <td>{{ g.employeeName }}</td>
              <td>{{ g.employeeIdentifier }}</td>
              <td>{{ g.companyName }}</td>
              <td>{{ g.conceptName }}</td>
              <td class="text-right">{{ formatAmount(g.totalAmount, g.currency) }}</td>
              <td>{{ g.paidCount }}/{{ g.totalInstallments }}</td>
              <td class="text-right">{{ formatAmount(g.pendingBalance, g.currency) }}</td>
              <td>
                <span :class="`badge ${getGroupStatusColor(g.status)}`">{{ getGroupStatusLabel(g.status) }}</span>
              </td>
              <td class="cell-center d_option pr-md-2 position-relative">
                <router-link
                  :to="{ name: 'movement-group-detail', params: { groupId: g.groupId } }"
                  class="text-primary"
                >
                  Ver detalle
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Mobile groups -->
        <div class="custom-table-mobile d-xl-none">
          <section class="custom-table-mobile-header">
            <div><span>Empleado</span></div>
            <div><span>Estado</span></div>
          </section>
          <div class="custom-table-mobile-body">
            <div class="custom-table-mobile-row" v-for="g in groups" :key="g.groupId">
              <div class="custom-table-mobile-grid">
                <div><span>{{ g.employeeName }}</span></div>
                <div>
                  <span :class="`badge ${getGroupStatusColor(g.status)}`">{{ getGroupStatusLabel(g.status) }}</span>
                </div>
              </div>
              <ul class="list-unstyled p-0 mt-2">
                <li><span class="font-weight-medium mr-2">Concepto:</span>{{ g.conceptName }}</li>
                <li><span class="font-weight-medium mr-2">Monto total:</span>{{ formatAmount(g.totalAmount, g.currency) }}</li>
                <li><span class="font-weight-medium mr-2">Cuotas:</span>{{ g.paidCount }}/{{ g.totalInstallments }}</li>
                <li><span class="font-weight-medium mr-2">Saldo:</span>{{ formatAmount(g.pendingBalance, g.currency) }}</li>
              </ul>
              <div class="custom-table-mobile-options mt-2">
                <router-link :to="{ name: 'movement-group-detail', params: { groupId: g.groupId } }" class="mx-3">Ver detalle</router-link>
              </div>
            </div>
          </div>
        </div>

        <div class="table-footer d-flex justify-content-end align-items-center">
          <div v-if="groups.length === 0" class="table-no-results">
            <p class="m-0 px-3">No se encontraron resultados.</p>
          </div>
          <p v-else class="m-0 px-3 text-muted small">{{ groups.length }} grupo(s)</p>
        </div>
      </template>
    </div>

    <ConfirmationModal
      :show="confirmationModal.show"
      @hide="closeConfirmationModal"
      :data="confirmationModal"
      @confirm="confirmModal"
    />
  </div>
</template>

<script>
import {
  Alert,
  DropdownTable,
  DropdownItem,
  ConfirmationModal,
  ConfirmationModalMixin,
  FormSelect,
  SearchableDropdown,
} from 'wize-admin'

import { Constants as LocalConstants } from '@/core/utils'
import { PayrollMovementService, PayrollConceptService, CompanyService } from '@/core/services'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

export default {
  name: 'PayrollMovementList',
  mixins: [ConfirmationModalMixin],
  components: { DropdownTable, DropdownItem, ConfirmationModal, FormSelect, SearchableDropdown },

  data() {
    const now = new Date()
    return {
      list:           [],
      groups:         [],
      showGroups:     false,
      downloading:    false,
      companyOptions: [],
      conceptOptions: [],
      typeFilterOptions: Object.values(LocalConstants.PayrollConceptType).map(t => ({ key: t.key, label: t.label })),
      monthOptions: MONTH_NAMES.map((m, i) => ({ key: i + 1, label: m })),
      filters: {
        month:     now.getMonth() + 1,
        year:      now.getFullYear(),
        companyId: null,
        type:      null,
        conceptId: null,
      },
    }
  },

  computed: {
    yearOptions() {
      const current = new Date().getFullYear()
      return Array.from({ length: 5 }, (_, i) => ({ key: current - 2 + i, label: String(current - 2 + i) }))
    },
  },

  methods: {
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const payload = { periodYear: this.filters.year, periodMonth: this.filters.month }
        if (this.filters.companyId) payload.companyId = this.filters.companyId
        if (this.filters.type)      payload.type      = this.filters.type
        if (this.filters.conceptId) payload.conceptId = this.filters.conceptId
        const resp = await PayrollMovementService.getByPeriod(payload)
        this.list = (resp.payload || []).map(item => ({ ...item, showMobileData: false }))
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los movimientos')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    async loadGroups() {
      try {
        await this.$store.dispatch('app/loading', true)
        const payload = {}
        if (this.filters.companyId) payload.companyId = this.filters.companyId
        const resp = await PayrollMovementService.getInstallmentGroups(payload)
        this.groups = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los grupos de cuotas')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    onToggleGroups(val) {
      if (val) {
        this.loadGroups()
      } else {
        this.loadData()
      }
    },

    async downloadExcel() {
      try {
        this.downloading = true
        const payload = { periodYear: this.filters.year, periodMonth: this.filters.month }
        if (this.filters.companyId) payload.companyId = this.filters.companyId
        if (this.filters.type)      payload.type      = this.filters.type
        if (this.filters.conceptId) payload.conceptId = this.filters.conceptId
        const resp = await PayrollMovementService.downloadListXlsx(payload)
        const url  = URL.createObjectURL(new Blob([resp.data]))
        const a    = document.createElement('a')
        a.href     = url
        a.download = `movimientos-${this.filters.year}-${String(this.filters.month).padStart(2, '0')}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el Excel')
      } finally {
        this.downloading = false
      }
    },

    onCompanySelected(id) { this.filters.companyId = id; if (!this.showGroups) this.loadData(); else this.loadGroups() },
    onConceptSelected(id) { this.filters.conceptId = id; this.loadData() },

    getTypeLabel(type) {
      const t = LocalConstants.PayrollConceptType[type]
      return t ? t.label : type
    },
    getTypeColor(type) {
      return type === 'DESCUENTO' ? 'badge-danger' : 'badge-green'
    },
    getNatureLabel(nature) {
      const n = LocalConstants.PayrollConceptNature[nature]
      return n ? n.label : nature
    },
    getOriginLabel(origin) {
      const o = LocalConstants.PayrollMovementOrigin[origin]
      return o ? o.label : origin
    },
    getStatusColor(status) {
      const s = LocalConstants.PayrollMovementStatus[status]
      return s ? s.color : 'badge-gray'
    },
    getGroupStatusLabel(status) {
      const s = LocalConstants.InstallmentGroupStatus[status]
      return s ? s.label : status
    },
    getGroupStatusColor(status) {
      const s = LocalConstants.InstallmentGroupStatus[status]
      return s ? s.color : 'badge-gray'
    },
    formatAmount(amount, currency) {
      if (amount == null) return '-'
      const formatted = Number(amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return `${currency || 'S/'} ${formatted}`
    },

    openCancel(item) {
      this.selectedOption = item
      this.openConfirmationModal({
        title:       'Cancelar movimiento',
        description: `¿Está seguro que desea cancelar el movimiento de ${item.employeeName} por ${this.formatAmount(item.amount, item.currency)}?`,
        acceptText:  'Cancelar movimiento',
        action:      'cancel',
      })
    },

    confirmModal() {
      this.closeConfirmationModal()
      if (this.confirmationModal.action === 'cancel') this.doCancel()
    },

    async doCancel() {
      try {
        await this.$store.dispatch('app/loading', true)
        await PayrollMovementService.cancel(this.selectedOption.id)
        await this.loadData()
        Alert.success('Movimiento cancelado correctamente')
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al cancelar el movimiento')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },

  async created() {
    try {
      const [conceptsResp, companiesResp] = await Promise.all([
        PayrollConceptService.listActive(),
        CompanyService.listAll(),
      ])
      this.conceptOptions = (conceptsResp.payload || []).map(c => ({ key: c.id, label: `${c.code} - ${c.name}` }))
      this.companyOptions = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.businessName || c.name }))
    } catch (e) {
      console.error('Error al cargar catálogos', e)
    }
    this.loadData()
  },
}
</script>

<style lang="stylus">
@import '../../Areas/Styles/supplier-list';
</style>
